import { useState, useEffect } from 'react';
import { getCallById } from '../../services/callService';
import { AudioPlayer } from './AudioPlayer';
import { EventsModal } from './EventsModal';
import { useProfileStore } from '../../store/useProfileStore';
import { formatDateTimeInTimezone } from './utils';
import type { MysteryShopCall } from '../../types/mysteryShop';

interface CallDataModalProps {
  callId: string;
  onClose: () => void;
}

export function CallDataModal({ callId, onClose }: CallDataModalProps) {
  const [call, setCall] = useState<MysteryShopCall | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [showEvents, setShowEvents] = useState(false);
  const { activeProfile } = useProfileStore();

  useEffect(() => {
    getCallById(callId).then(setCall);
  }, [callId]);

  const fields = call
    ? [
        { label: 'Retell Call Id', value: call.extCallId || '—' },
        { label: 'Date', value: formatDateTimeInTimezone(call.date, activeProfile?.timezone) },
        { label: 'AI Agent Id', value: call.retellAgentId || '—' },
        { label: 'From Number', value: call.fromNumber || '—' },
        { label: 'To Number', value: call.toNumber || '—' },
        { label: 'Call Successful', value: String(call.callSuccessful ?? false) },
        { label: 'Voicemail', value: String(call.voicemail ?? false) },
      ]
    : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-lg w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4">
          <h2 className="text-lg font-bold text-gray-900">Call Data</h2>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 pb-6 overflow-y-auto flex-1">
          {!call ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : (
            <div className="space-y-4">
              {/* Data fields */}
              <div className="space-y-2">
                {fields.map((f) => (
                  <div key={f.label} className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-gray-500 min-w-[110px]">{f.label}:</span>
                    <span className="text-sm text-gray-900">{f.value}</span>
                  </div>
                ))}
              </div>

              {/* Call Summary */}
              {call.callSummary && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <div className="p-4">
                    <h3 className="font-medium text-gray-800 mb-3">Call Summary</h3>
                    <div className="bg-gray-50 rounded-lg p-4 text-sm space-y-1.5">
                      {call.callSummary.split('\n').filter(Boolean).map((line, i) => (
                        <p key={i} className="text-gray-800">{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Audio player */}
              {call.recordingUrl && (
                <AudioPlayer src={call.recordingUrl} />
              )}

              {/* Transcript */}
              {call.transcript && (
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  <button
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() => setShowTranscript(!showTranscript)}
                  >
                    <h3 className="font-medium text-gray-800">Call Transcript</h3>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${showTranscript ? 'rotate-180' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {showTranscript && (
                    <div className="px-4 pb-4 border-t border-gray-100">
                      <div className="bg-gray-50 rounded-lg p-4 text-sm mt-3 space-y-1.5">
                        {call.transcript.split('\n').filter(Boolean).map((line, i) => {
                          const isUser = line.startsWith('User: ');
                          return (
                            <p key={i} className={isUser ? 'text-black' : 'text-gray-800 italic'}>
                              {line}
                            </p>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* View Events button */}
              <button
                onClick={() => setShowEvents(true)}
                className="w-full text-sm px-3 py-2 rounded-lg text-white transition-colors"
                style={{ backgroundColor: 'var(--ion-color-secondary)' }}
              >
                View Events
              </button>
            </div>
          )}
        </div>
      </div>

      {showEvents && call?.events && (
        <EventsModal events={call.events} onClose={() => setShowEvents(false)} />
      )}
    </div>
  );
}
